import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { dispatcherService } from '../../services/dispatcherService';
import DispatcherForm from '../../components/dispatchers/DispatcherForm';
import { ChevronLeft } from 'lucide-react';

const DispatcherFormPage = () => {
    const { dispatcherId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dispatcher, setDispatcher] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const isEditing = !!dispatcherId;

    useEffect(() => {
        if (isEditing) {
            fetchDispatcherDetails();
        }
    }, [dispatcherId]);

    const fetchDispatcherDetails = async () => {
        setIsLoading(true);
        try {
            const dispatcherData = await dispatcherService.getDispatcherById(dispatcherId);
            setDispatcher(dispatcherData);
        } catch (error) {
            console.error('Error fetching dispatcher details:', error);
            setErrorMessage('無法獲取調度員資訊，請稍後再試。');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (formData) => {
        setIsSubmitting(true);
        setErrorMessage('');

        try {
            if (isEditing) {
                await dispatcherService.updateDispatcher(dispatcherId, formData);
            } else {
                await dispatcherService.createDispatcher(formData);
            }

            // 操作成功，返回調度員列表頁面
            navigate('/dispatchers', {
                state: {
                    message: isEditing ? '調度員已成功更新' : '新調度員已成功建立',
                    type: 'success'
                }
            });
        } catch (error) {
            console.error('Error saving dispatcher:', error);
            setErrorMessage(error.response?.data?.message || '儲存失敗，請檢查表單並重試。');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center mb-6">
                <button
                    onClick={() => navigate('/dispatchers')}
                    className="p-2 mr-2 rounded-md hover:bg-gray-100"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
                <h1 className="text-2xl font-bold">
                    {isEditing ? '編輯調度員' : '新增調度員'}
                </h1>
            </div>

            {errorMessage && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-6">
                    {errorMessage}
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="p-6">
                    <DispatcherForm
                        initialData={dispatcher}
                        onSubmit={handleSubmit}
                        isEditing={isEditing}
                        isSubmitting={isSubmitting}
                    />
                </div>
            </div>
        </div>
    );
};

export default DispatcherFormPage;